// The activity report — ONE screen for every place activity is reviewed:
// the builder's Advanced → Activity (one application), Instance admin →
// Activity (everything), the Run view's System administration console and the
// downloaded runtime's Activity page (the application's own administrators).
//
// Each host supplies only an adapter (how to fetch `meta` and a report) and,
// optionally, which realms the viewer may pick. All numbers are computed on
// the server (twasta_runtime_engine.activity.query); this only lays them out.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'

export interface ActivityMeta {
  dimensions: { name: string; label: string }[]
  events: { name: string; category: string; label: string }[]
  realms: string[]
  geo_attribution?: string
  status?: Record<string, unknown>
  project_settings?: Record<string, unknown>
}

export interface ActivityReportAdapter {
  meta(): Promise<ActivityMeta>
  report(kind: string, params: Record<string, string>): Promise<{ data: unknown }>
}

export interface ActivityReportProps {
  adapter: ActivityReportAdapter
  /** Realms the viewer may filter by; absent = all the server lists. */
  realms?: string[]
  /** Pre-selected realm ('' = all). */
  defaultRealm?: string
  /** Show the Application column (instance-wide / org-wide views). */
  showProject?: boolean
  compact?: boolean
}

type Row = Record<string, unknown>

const REALM_LABEL: Record<string, string> = {
  app: 'Application users', site: 'Website visitors', builder: 'Builders (Twasta users)',
}

const TABS: { id: string; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'pages', label: 'Pages & screens' },
  { id: 'actions', label: 'Actions' },
  { id: 'people', label: 'People' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'sources', label: 'Sources & technology' },
  { id: 'locations', label: 'Locations' },
  { id: 'ai', label: 'AI questions' },
  { id: 'live', label: 'Live' },
]

const RANGES: { id: string; label: string; days: number }[] = [
  { id: 'today', label: 'Today', days: 0 },
  { id: '7', label: 'Last 7 days', days: 6 },
  { id: '30', label: 'Last 30 days', days: 29 },
  { id: '90', label: 'Last 90 days', days: 89 },
  { id: '365', label: 'Last 12 months', days: 364 },
]

// ── formatting ─────────────────────────────────────────────────────────

const C = {
  text: 'var(--color-text-primary, #0f172a)',
  muted: 'var(--color-text-secondary, #64748b)',
  border: 'var(--color-border, #e2e8f0)',
  bg: 'var(--color-bg-secondary, #f8fafc)',
  card: 'var(--color-bg-primary, #ffffff)',
  accent: 'var(--color-accent, #4f46e5)',
  error: 'var(--color-error, #dc2626)',
}

function isoDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function formatDuration(ms: unknown): string {
  const n = Number(ms)
  if (!Number.isFinite(n) || n <= 0) return '—'
  const s = Math.round(n / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

function fmtNum(v: unknown): string {
  const n = Number(v)
  if (v === null || v === undefined || !Number.isFinite(n)) return '—'
  return n.toLocaleString()
}

function fmtPct(v: unknown): string {
  const n = Number(v)
  if (v === null || v === undefined || !Number.isFinite(n)) return '—'
  return `${Math.round(n * 100)}%`
}

function fmtTime(v: unknown): string {
  if (!v) return '—'
  const d = new Date(String(v))
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString()
}

function errText(e: unknown): string {
  const x = e as { response?: { data?: { detail?: unknown } }; message?: string; detail?: unknown } | null
  const d = x?.response?.data?.detail ?? x?.detail
  if (typeof d === 'string' && d) return d
  return x?.message || 'The report could not be loaded.'
}

function toCsv(rows: Row[], cols: Column[]): string {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [cols.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => cols.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(','))].join('\n')
}

function download(name: string, text: string) {
  try {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = name
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch { /* download blocked */ }
}

// ── small building blocks ──────────────────────────────────────────────

interface Column {
  key: string
  label: string
  align?: 'right'
  render?: (r: Row) => ReactNode
  csv?: (r: Row) => unknown
  width?: number
  /** Exported, not shown. */
  csvOnly?: boolean
}

function Table({ rows, cols: allCols, onRow, empty = 'Nothing recorded in this period.', csvName }: {
  rows: Row[]; cols: Column[]; onRow?: (r: Row) => void; empty?: string; csvName?: string
}) {
  const cols = allCols.filter((c) => !c.csvOnly)
  if (!rows.length) return <div style={{ padding: 16, color: C.muted, fontSize: 14 }}>{empty}</div>
  const th: CSSProperties = {
    textAlign: 'left', padding: '6px 10px', fontSize: 13, fontWeight: 600, color: C.muted,
    textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${C.border}`,
    position: 'sticky', top: 0, background: C.card, whiteSpace: 'nowrap',
  }
  const td: CSSProperties = { padding: '7px 10px', fontSize: 14, borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' }
  return (
    <div>
      {csvName && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button type="button" style={linkBtn} onClick={() => download(csvName, toCsv(rows, allCols))}>
            Download CSV
          </button>
        </div>
      )}
      <div style={{ overflow: 'auto', maxHeight: 520, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', color: C.text }}>
          <thead>
            <tr>{cols.map((c) => <th key={c.key} style={{ ...th, textAlign: c.align || 'left', width: c.width }}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onClick={onRow ? () => onRow(r) : undefined}
                  style={{ cursor: onRow ? 'pointer' : undefined }}>
                {cols.map((c) => (
                  <td key={c.key} style={{ ...td, textAlign: c.align || 'left' }}>
                    {c.render ? c.render(r) : (r[c.key] === null || r[c.key] === undefined || r[c.key] === ''
                      ? <span style={{ color: C.muted }}>—</span> : String(r[c.key]))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const linkBtn: CSSProperties = {
  background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 13, padding: 0,
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', background: C.card, minWidth: 0 }}
         title={hint}>
      <div style={{ fontSize: 13, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 23, fontWeight: 600, color: C.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  )
}

function Section({ title, children, right }: { title: string; children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</div>
        <div style={{ marginLeft: 'auto' }}>{right}</div>
      </div>
      {children}
    </div>
  )
}

/** Horizontal bars — one row per value, share of the largest. */
function Bars({ rows, labelKey = 'value', valueKey = 'events', format = fmtNum, empty }: {
  rows: Row[]; labelKey?: string; valueKey?: string; format?: (v: unknown) => string; empty?: string
}) {
  if (!rows.length) return <div style={{ color: C.muted, fontSize: 14 }}>{empty || 'Nothing recorded.'}</div>
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1)
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0
        const label = r[labelKey] === null || r[labelKey] === undefined || r[labelKey] === '' ? '(none)' : String(r[labelKey])
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <div style={{ position: 'relative', padding: '3px 8px', borderRadius: 4, overflow: 'hidden', color: C.text }}>
              <div style={{ position: 'absolute', inset: 0, width: `${(v / max) * 100}%`, background: C.accent, opacity: 0.14 }} />
              <span style={{ position: 'relative', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{label}</span>
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums', color: C.muted }}>{format(r[valueKey])}</div>
          </div>
        )
      })}
    </div>
  )
}

/** A simple area/line chart over the timeseries buckets. */
function TrendChart({ rows, series }: { rows: Row[]; series: { key: string; label: string; opacity: number }[] }) {
  const [hover, setHover] = useState<number | null>(null)
  if (!rows.length) return <div style={{ color: C.muted, fontSize: 14, padding: 12 }}>Nothing recorded in this period.</div>
  const W = 720
  const H = 180
  const P = { l: 36, r: 8, t: 10, b: 22 }
  const max = Math.max(1, ...rows.flatMap((r) => series.map((s) => Number(r[s.key]) || 0)))
  const x = (i: number) => P.l + (rows.length === 1 ? (W - P.l - P.r) / 2 : (i * (W - P.l - P.r)) / (rows.length - 1))
  const y = (v: number) => P.t + (H - P.t - P.b) * (1 - v / max)
  const ticks = [0, Math.round(max / 2), max]
  const label = (b: unknown) => String(b || '').replace(' 00:00', '')
  const step = Math.max(1, Math.ceil(rows.length / 8))
  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}
           onMouseLeave={() => setHover(null)}
           onMouseMove={(e) => {
             const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
             const px = ((e.clientX - rect.left) / rect.width) * W
             let best = 0
             rows.forEach((_, i) => { if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i })
             setHover(best)
           }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="currentColor" opacity={0.08} />
            <text x={P.l - 6} y={y(t) + 4} textAnchor="end" fontSize={10} fill="currentColor" opacity={0.55}>{t.toLocaleString()}</text>
          </g>
        ))}
        {series.map((s) => {
          const pts = rows.map((r, i) => `${x(i)},${y(Number(r[s.key]) || 0)}`)
          return (
            <g key={s.key}>
              <polygon points={`${x(0)},${y(0)} ${pts.join(' ')} ${x(rows.length - 1)},${y(0)}`}
                       fill={C.accent} opacity={s.opacity * 0.25} />
              <polyline points={pts.join(' ')} fill="none" stroke={C.accent} strokeWidth={2} opacity={s.opacity} />
            </g>
          )
        })}
        {rows.map((r, i) => (i % step === 0 || i === rows.length - 1) && (
          <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize={10} fill="currentColor" opacity={0.55}>
            {label(r.bucket).slice(5)}
          </text>
        ))}
        {/* A point per bucket — on its own a single bucket draws no line. */}
        {series.map((s) => rows.map((r, i) => (
          <circle key={`${s.key}-${i}`} cx={x(i)} cy={y(Number(r[s.key]) || 0)} r={rows.length <= 31 ? 3 : 0}
                  fill={C.accent} opacity={s.opacity} />
        )))}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={P.t} y2={H - P.b} stroke="currentColor" opacity={0.25} />}
      </svg>
      <div style={{ display: 'flex', gap: 14, fontSize: 13, color: C.muted, marginTop: 4, flexWrap: 'wrap' }}>
        {series.map((s) => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 3, background: C.accent, opacity: s.opacity, display: 'inline-block' }} />
            {s.label}{hover !== null ? `: ${fmtNum(rows[hover][s.key])}` : ''}
          </span>
        ))}
        {hover !== null && <span>{label(rows[hover].bucket)}</span>}
      </div>
    </div>
  )
}

// ── the report ─────────────────────────────────────────────────────────

export default function ActivityReport({ adapter, realms, defaultRealm = '', showProject = false, compact = false }: ActivityReportProps) {
  const [meta, setMeta] = useState<ActivityMeta | null>(null)
  const [tab, setTab] = useState('overview')
  const [range, setRange] = useState('30')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [realm, setRealm] = useState(defaultRealm)
  const [bots, setBots] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, unknown>>({})
  const [detail, setDetail] = useState<{ title: string; params: Record<string, string> } | null>(null)
  const [detailRows, setDetailRows] = useState<Row[] | null>(null)
  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
  }, [])

  useEffect(() => {
    adapter.meta().then(setMeta).catch((e) => setError(errText(e)))
  }, [adapter])

  const span = useMemo(() => {
    if (range === 'custom' && from && to) return { from, to }
    const r = RANGES.find((x) => x.id === range) || RANGES[2]
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - r.days)
    return { from: isoDay(start), to: isoDay(end) }
  }, [range, from, to])

  const base = useMemo(() => {
    const p: Record<string, string> = { from: span.from, to: span.to, tz }
    if (realm) p.realm = realm
    if (bots) p.bots = '1'
    return p
  }, [span, tz, realm, bots])

  const reqId = useRef(0)
  const load = useCallback(async () => {
    const id = ++reqId.current
    setLoading(true)
    setError(null)
    const get = (kind: string, extra: Record<string, string> = {}) =>
      adapter.report(kind, { ...base, ...extra }).then((r) => r.data)
    const dim = (d: string, limit = '10') => get('breakdown', { dimension: d, limit })
    try {
      let out: Record<string, unknown> = {}
      const daysSpan = (new Date(span.to).getTime() - new Date(span.from).getTime()) / 86400000
      if (tab === 'overview') {
        const [overview, series, pages, countries, devices, referrers] = await Promise.all([
          get('overview'), get('timeseries', { unit: daysSpan <= 2 ? 'hour' : 'day' }),
          get('pages', { limit: '8' }), dim('country', '8'), dim('device_type', '6'), dim('referrer_host', '8'),
        ])
        out = { overview, series, pages, countries, devices, referrers }
      } else if (tab === 'pages') {
        out = { pages: await get('pages', { limit: '500' }) }
      } else if (tab === 'actions') {
        out = { actions: await get('actions', { limit: '500' }) }
      } else if (tab === 'people') {
        out = { people: await get('people', { limit: '1000' }) }
      } else if (tab === 'sessions') {
        out = { sessions: await get('sessions', { limit: '1000' }) }
      } else if (tab === 'sources') {
        const keys = ['referrer_host', 'utm_source', 'utm_medium', 'utm_campaign', 'browser', 'os', 'device_type', 'device_brand', 'screen', 'language', 'surface']
        const res = await Promise.all(keys.map((k) => dim(k, '15')))
        out = Object.fromEntries(keys.map((k, i) => [k, res[i]]))
      } else if (tab === 'locations') {
        const [country, region, city, ip, tzs] = await Promise.all([
          dim('country', '50'), dim('region', '50'), dim('city', '50'), dim('ip', '100'), dim('timezone', '20'),
        ])
        out = { country, region, city, ip, timezone: tzs }
      } else if (tab === 'ai') {
        out = { ai: await get('ai', { limit: '1000' }) }
      } else if (tab === 'live') {
        out = { live: await get('live') }
      }
      if (id === reqId.current) setData(out)
    } catch (e) {
      if (id === reqId.current) setError(errText(e))
    } finally {
      if (id === reqId.current) setLoading(false)
    }
  }, [adapter, base, tab, span])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (tab !== 'live') return undefined
    const t = setInterval(() => { void load() }, 15000)
    return () => clearInterval(t)
  }, [tab, load])

  // The drill-down: one person's or one session's trail.
  useEffect(() => {
    if (!detail) { setDetailRows(null); return }
    let cancelled = false
    setDetailRows(null)
    adapter.report('timeline', { ...base, ...detail.params, limit: '2000' })
      .then((r) => { if (!cancelled) setDetailRows((r.data as Row[]) || []) })
      .catch((e) => { if (!cancelled) { setDetailRows([]); setError(errText(e)) } })
    return () => { cancelled = true }
  }, [detail, adapter, base])

  const eventLabel = useMemo(() => {
    const m = new Map((meta?.events || []).map((e) => [e.name, e.label]))
    return (n: unknown) => m.get(String(n)) || String(n || '')
  }, [meta])

  const realmChoices = (realms || meta?.realms || ['app', 'site', 'builder'])
  const rows = (k: string) => ((data[k] as Row[]) || [])
  const ov = (data.overview || {}) as Row

  const projectCol: Column[] = showProject ? [{ key: 'project', label: 'Application' }] : []
  const personCell = (r: Row) => (
    <span>
      <span style={{ fontWeight: 500 }}>{String(r.user_label || '') || (r.user_id ? String(r.user_id) : 'Anonymous visitor')}</span>
      {!r.user_label && r.visitor_id ? <span style={{ color: C.muted, fontSize: 13 }}> · {String(r.visitor_id).slice(0, 8)}</span> : null}
      {r.user_kind ? <span style={{ color: C.muted, fontSize: 13 }}> · {String(r.user_kind).replace('_', ' ')}</span> : null}
    </span>
  )
  const where = (r: Row) => [r.city, r.region, r.country].filter(Boolean).join(', ') || '—'
  const device = (r: Row) => [r.device_type, r.browser, r.os].filter(Boolean).join(' · ') || '—'

  // Explicit width: host stylesheets (the platform's form styles) stretch a
  // bare <select>/<input> to the full row.
  const control: CSSProperties = {
    fontSize: 14, padding: '5px 8px', borderRadius: 6, border: `1px solid ${C.border}`,
    background: C.card, color: C.text, width: 'auto', minWidth: 0, flex: '0 0 auto',
  }

  return (
    <div style={{ color: C.text, fontSize: 14 }}>
      {/* ── filters ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <select style={control} value={range} onChange={(e) => setRange(e.target.value)} aria-label="Period">
          {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          <option value="custom">Custom…</option>
        </select>
        {range === 'custom' && (
          <>
            <input type="date" style={control} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
            <input type="date" style={control} value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
          </>
        )}
        {realmChoices.length > 1 && (
          <select style={control} value={realm} onChange={(e) => setRealm(e.target.value)} aria-label="Who">
            <option value="">Everyone</option>
            {realmChoices.map((r) => <option key={r} value={r}>{REALM_LABEL[r] || r}</option>)}
          </select>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.muted }}>
          <input type="checkbox" checked={bots} onChange={(e) => setBots(e.target.checked)} /> Include bots
        </label>
        <span style={{ marginLeft: 'auto', color: C.muted, fontSize: 13 }}>
          {loading ? 'Loading…' : `${span.from} → ${span.to} · ${tz}`}
        </span>
        <button type="button" style={linkBtn} onClick={() => void load()}>Refresh</button>
      </div>

      {/* ── tabs ── */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, overflowX: 'auto', marginBottom: 12 }}>
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: compact ? '6px 8px' : '8px 10px',
              fontSize: 14, whiteSpace: 'nowrap', color: tab === t.id ? C.accent : C.muted,
              fontWeight: tab === t.id ? 600 : 500, marginBottom: -1,
              borderBottom: `2px solid ${tab === t.id ? C.accent : 'transparent'}`,
            }}>{t.label}</button>
        ))}
      </div>

      {error && (
        <div style={{ padding: 10, borderRadius: 6, border: `1px solid ${C.error}`, color: C.error, marginBottom: 10 }}>{error}</div>
      )}

      {tab === 'overview' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 130 : 150}px, 1fr))`, gap: 8 }}>
            <Kpi label="People" value={fmtNum(ov.people)} hint="Signed-in users plus anonymous visitors" />
            <Kpi label="Signed-in users" value={fmtNum(ov.signed_in_users)} />
            <Kpi label="Anonymous visitors" value={fmtNum(ov.anonymous_visitors)} />
            <Kpi label="Sessions" value={fmtNum(ov.sessions)} />
            <Kpi label="Page / screen views" value={fmtNum(ov.views)} />
            <Kpi label="Avg time on page" value={formatDuration(ov.avg_view_ms)} hint="Visible time per view" />
            <Kpi label="Avg engaged time" value={formatDuration(ov.avg_engaged_ms)} hint="Visible and interacting" />
            <Kpi label="Avg session" value={formatDuration(ov.avg_session_ms)} />
            <Kpi label="Bounce rate" value={fmtPct(ov.bounce_rate)} hint="Sessions with a single view" />
            <Kpi label="Actions" value={fmtNum(ov.actions)} hint="Saves, deletes, buttons, clicks, forms…" />
            <Kpi label="Ask-AI prompts" value={fmtNum(ov.ai_queries)} />
            <Kpi label="Assistant questions" value={fmtNum(ov.assistant_queries)} />
          </div>
          <Section title="Over time">
            <TrendChart rows={rows('series')} series={[
              { key: 'views', label: 'Views', opacity: 1 },
              { key: 'people', label: 'People', opacity: 0.5 },
            ]} />
          </Section>
          <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
            <Section title="Top pages & screens">
              <Bars rows={rows('pages')} labelKey="name" valueKey="views" />
            </Section>
            <Section title="Countries">
              <Bars rows={rows('countries')} valueKey="people" />
            </Section>
            <Section title="Devices">
              <Bars rows={rows('devices')} valueKey="people" />
            </Section>
            <Section title="Referrers">
              <Bars rows={rows('referrers').filter((r) => r.value)} valueKey="sessions" empty="No external referrers." />
            </Section>
          </div>
        </>
      )}

      {tab === 'pages' && (
        <Table csvName="activity-pages.csv" rows={rows('pages')} onRow={(r) => setDetail({ title: `Views of ${r.name}`, params: { name: String(r.name), category: 'view' } })} cols={[
          { key: 'name', label: 'Page / screen', render: (r) => <span><b style={{ fontWeight: 500 }}>{String(r.name)}</b>{r.title && r.title !== r.name ? <span style={{ color: C.muted }}> · {String(r.title)}</span> : null}</span> },
          { key: 'obj_type', label: 'Type' },
          { key: 'views', label: 'Views', align: 'right', render: (r) => fmtNum(r.views) },
          { key: 'people', label: 'People', align: 'right', render: (r) => fmtNum(r.people) },
          { key: 'avg_time_ms', label: 'Avg time', align: 'right', render: (r) => formatDuration(r.avg_time_ms) },
          { key: 'avg_engaged_ms', label: 'Engaged', align: 'right', render: (r) => formatDuration(r.avg_engaged_ms) },
          { key: 'avg_scroll_pct', label: 'Scroll', align: 'right', render: (r) => (r.avg_scroll_pct == null ? '—' : `${Math.round(Number(r.avg_scroll_pct))}%`) },
          { key: 'actions', label: 'Actions', align: 'right', render: (r) => fmtNum(r.actions) },
          { key: 'last_seen', label: 'Last seen', render: (r) => fmtTime(r.last_seen) },
        ]} />
      )}

      {tab === 'actions' && (
        <Table csvName="activity-actions.csv" rows={rows('actions')} cols={[
          { key: 'event', label: 'What', render: (r) => eventLabel(r.event), csv: (r) => eventLabel(r.event) },
          { key: 'name', label: 'Where' },
          { key: 'label', label: 'Detail' },
          { key: 'count', label: 'Times', align: 'right', render: (r) => fmtNum(r.count) },
          { key: 'people', label: 'People', align: 'right', render: (r) => fmtNum(r.people) },
          { key: 'failures', label: 'Failed', align: 'right', render: (r) => (Number(r.failures) ? <span style={{ color: C.error }}>{fmtNum(r.failures)}</span> : '0') },
          { key: 'avg_duration_ms', label: 'Avg duration', align: 'right', render: (r) => formatDuration(r.avg_duration_ms) },
          { key: 'last_seen', label: 'Last', render: (r) => fmtTime(r.last_seen) },
        ]} />
      )}

      {tab === 'people' && (
        <Table csvName="activity-people.csv" rows={rows('people')} onRow={(r) => setDetail({ title: String(r.user_label || r.person), params: { person: String(r.person) } })} cols={[
          { key: 'person', label: 'Who', render: personCell, csv: (r) => r.user_label || r.person },
          ...projectCol,
          { key: 'sessions', label: 'Sessions', align: 'right', render: (r) => fmtNum(r.sessions) },
          { key: 'views', label: 'Views', align: 'right', render: (r) => fmtNum(r.views) },
          { key: 'actions', label: 'Actions', align: 'right', render: (r) => fmtNum(r.actions) },
          { key: 'ai_queries', label: 'AI', align: 'right', render: (r) => fmtNum(r.ai_queries) },
          { key: 'total_time_ms', label: 'Time spent', align: 'right', render: (r) => formatDuration(r.total_time_ms) },
          { key: 'where', label: 'Location', render: where, csv: where },
          { key: 'ip', label: 'IP' },
          { key: 'device', label: 'Device', render: device, csv: device },
          { key: 'first_seen', label: 'First seen', render: (r) => fmtTime(r.first_seen) },
          { key: 'last_seen', label: 'Last seen', render: (r) => fmtTime(r.last_seen) },
        ]} />
      )}

      {tab === 'sessions' && (
        <Table csvName="activity-sessions.csv" rows={rows('sessions')} onRow={(r) => setDetail({ title: `Session ${String(r.session_id).slice(0, 8)} · ${String(r.user_label || 'visitor')}`, params: { session: String(r.session_id) } })} cols={[
          { key: 'started', label: 'Started', render: (r) => fmtTime(r.started) },
          { key: 'person', label: 'Who', render: personCell, csv: (r) => r.user_label || r.person },
          { key: 'duration_ms', label: 'Length', align: 'right', render: (r) => formatDuration(r.duration_ms) },
          { key: 'views', label: 'Views', align: 'right', render: (r) => fmtNum(r.views) },
          { key: 'actions', label: 'Actions', align: 'right', render: (r) => fmtNum(r.actions) },
          { key: 'landing', label: 'Landed on' },
          { key: 'exit_page', label: 'Left from' },
          { key: 'referrer_host', label: 'Came from' },
          { key: 'utm_source', label: 'Campaign source' },
          { key: 'where', label: 'Location', render: where, csv: where },
          { key: 'ip', label: 'IP' },
          { key: 'device', label: 'Device', render: device, csv: device },
        ]} />
      )}

      {tab === 'sources' && (
        <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          {([
            ['referrer_host', 'Referrers'], ['utm_source', 'Campaign source'], ['utm_medium', 'Campaign medium'],
            ['utm_campaign', 'Campaign'], ['browser', 'Browsers'], ['os', 'Operating systems'],
            ['device_type', 'Device types'], ['device_brand', 'Device brands'], ['screen', 'Screen sizes'],
            ['language', 'Languages'], ['surface', 'Surface'],
          ] as [string, string][]).map(([k, title]) => (
            <Section key={k} title={title}>
              <Bars rows={rows(k)} valueKey="sessions" />
            </Section>
          ))}
        </div>
      )}

      {tab === 'locations' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
            <Section title="Countries"><Bars rows={rows('country')} valueKey="people" /></Section>
            <Section title="Regions"><Bars rows={rows('region').slice(0, 20)} valueKey="people" /></Section>
            <Section title="Cities"><Bars rows={rows('city').slice(0, 20)} valueKey="people" /></Section>
            <Section title="Time zones"><Bars rows={rows('timezone')} valueKey="people" /></Section>
          </div>
          <Section title="IP addresses">
            <Table csvName="activity-ips.csv" rows={rows('ip')} cols={[
              { key: 'value', label: 'IP address' },
              { key: 'people', label: 'People', align: 'right', render: (r) => fmtNum(r.people) },
              { key: 'sessions', label: 'Sessions', align: 'right', render: (r) => fmtNum(r.sessions) },
              { key: 'events', label: 'Events', align: 'right', render: (r) => fmtNum(r.events) },
            ]} />
          </Section>
          {meta?.geo_attribution && (
            <div style={{ fontSize: 13, color: C.muted, marginTop: 10 }}>{meta.geo_attribution}</div>
          )}
        </>
      )}

      {tab === 'ai' && (
        <Table csvName="activity-ai-questions.csv" rows={rows('ai').filter((r) => r.event !== 'ai_result')} cols={[
          { key: 'ts', label: 'When', render: (r) => fmtTime(r.ts) },
          { key: 'event', label: 'Asked', render: (r) => (r.event === 'assistant_query' ? 'Assistant' : `Ask-AI${r.engine ? ` (${String(r.engine)})` : ''}`), csv: (r) => r.event },
          { key: 'user_label', label: 'Who', render: (r) => String(r.user_label || r.user_id || 'Anonymous') },
          ...projectCol,
          { key: 'question', label: 'Question / prompt', render: (r) => <span style={{ whiteSpace: 'pre-wrap', display: 'block', maxWidth: 520 }}>{String(r.question || '')}</span> },
          { key: 'name', label: 'Context' },
          { key: 'model', label: 'Model' },
          { key: 'tokens', label: 'Tokens', align: 'right', render: (r) => (r.tokens_in || r.tokens_out ? `${fmtNum(r.tokens_in)} / ${fmtNum(r.tokens_out)}` : '—'), csv: (r) => `${r.tokens_in ?? ''}/${r.tokens_out ?? ''}` },
          { key: 'duration_ms', label: 'Took', align: 'right', render: (r) => formatDuration(r.duration_ms) },
          { key: 'status', label: 'Status' },
        ]} empty="No Ask-AI prompts or Assistant questions in this period." />
      )}

      {tab === 'live' && (
        <>
          <div style={{ color: C.muted, marginBottom: 8 }}>
            People active in the last 5 minutes — refreshes every 15 seconds.
          </div>
          <Table rows={rows('live')} onRow={(r) => setDetail({ title: String(r.user_label || r.person), params: { person: String(r.person) } })} cols={[
            { key: 'person', label: 'Who', render: personCell },
            ...projectCol,
            { key: 'current', label: 'On' },
            { key: 'realm', label: 'Area', render: (r) => REALM_LABEL[String(r.realm)] || String(r.realm || '') },
            { key: 'where', label: 'Location', render: where },
            { key: 'device_type', label: 'Device' },
            { key: 'last_seen', label: 'Last activity', render: (r) => fmtTime(r.last_seen) },
          ]} empty="Nobody is active right now." />
        </>
      )}

      {/* ── drill-down: one person's / one session's trail ── */}
      {detail && (
        <div role="dialog" aria-label={detail.title}
             style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}
             onClick={() => setDetail(null)}>
          <div style={{ width: 'min(760px, 100vw)', height: '100%', background: C.card, padding: 18, overflow: 'auto', boxShadow: '-8px 0 24px rgba(0,0,0,0.15)' }}
               onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{detail.title}</div>
              <button type="button" onClick={() => setDetail(null)} style={{ ...linkBtn, marginLeft: 'auto', fontSize: 15 }}>Close</button>
            </div>
            {detailRows === null
              ? <div style={{ color: C.muted }}>Loading…</div>
              : (
                <Table csvName="activity-timeline.csv" rows={detailRows} cols={[
                  { key: 'ts', label: 'When', render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{fmtTime(r.ts)}</span>, csv: (r) => fmtTime(r.ts) },
                  { key: 'event', label: 'What', render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{eventLabel(r.event)}</span>, csv: (r) => eventLabel(r.event) },
                  { key: 'name', label: 'Where', render: (r) => String(r.name || r.path || '—') },
                  { key: 'label', label: 'Detail', render: (r) => <span style={{ whiteSpace: 'pre-wrap', display: 'block', maxWidth: 320 }}>{String(r.label || r.title || '') || '—'}</span> },
                  { key: 'duration_ms', label: 'Time', align: 'right', render: (r) => formatDuration(r.duration_ms) },
                  { key: 'status', label: 'Status' },
                  ...projectCol,
                  // Location / IP / device go to the CSV only — on screen they
                  // repeat on every row of one person's trail.
                  { key: 'where', label: 'Location', render: where, csv: where, csvOnly: true },
                  { key: 'ip', label: 'IP', csvOnly: true },
                  { key: 'device', label: 'Device', render: device, csv: device, csvOnly: true },
                ]} empty="No events." />
              )}
          </div>
        </div>
      )}
    </div>
  )
}
